<script setup lang="ts">
import { ISidebarItem } from '@/interfaces/components/sidebar/sidebarItem'
import { UserRoles } from '@/interfaces/enums'
import { useSidebarStore } from '@/store/sidebar'
import { useUserStore } from '@/store/user'
import { useCompanyStore } from '@/store/company'
import { onClickOutside, useWindowSize } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import CompanySelector from '../Globals/CompanySelector.vue'
import Logo from '../Globals/Logo.vue'
import DropdownUser from '../Header/DropdownUser.vue'
import SidebarItem from './SidebarItem.vue'

const target = ref(null)
const sidebarStore = useSidebarStore()
const { width } = useWindowSize()
const AUTO_COLLAPSE_BREAKPOINT = 1300

onClickOutside(target, () => {
  if (window.innerWidth < 1024) {
    sidebarStore.closeSidebar()
  }
})

watch(
  width,
  (currentWidth) => {
    if (currentWidth < AUTO_COLLAPSE_BREAKPOINT) {
      sidebarStore.setCollapsed(true)
    }
  },
  { immediate: true },
)

const userStore = useUserStore()

const canAccessManagerRole = computed(() => {
  const role = userStore.getCurrentUser?.role as string | undefined
  return role === UserRoles.ADMIN || role === UserRoles.MANAGER
})

const baseMenuItems: ISidebarItem[] = [
      {
        label: 'Dashboards',
        route: '#',
        iconName: 'dashboard',
        activeMatchers: ['/dashboard', '/grafana-dashboards', '/temperatura-diaria'],
        children: [
          { label: 'Visão geral', route: '/dashboard' },
          { label: 'Administrativos', route: '/grafana-dashboards' },
          { label: 'Temperatura Diária', route: '/temperatura-diaria' },
          { label: 'Personalizados', route: '/custom-dashboards' },
        ],
      },
      {
        label: 'Grupos',
        route: '#',
        iconName: 'groups',
        children: [
          { label: 'Listar', route: '/list-groups' },
          { label: 'Criar', route: '/create-group' },
          { label: 'Notificação', route: '/user-profile/notification-groups' },
        ],
      },
      {
        label: 'Relatório',
        route: '/report',
        iconName: 'report',
      },
      {
        label: 'Sensores',
        route: '/list-sensors',
				activeMatchers: ['/sensors/'],
				iconName: 'sensors',
        children: [
          { label: 'Listar sensores', route: '/list-sensors' },
          { label: 'Criar sensor', route: '/create-sensor' },
        ]
      },
      {
        label: 'Registros',
        route: '/registers',
        iconName: 'registers',
      },
      {
        label: 'Rateio',
        route: '/rate',
        iconName: 'rate',
      },
      {
        label: 'Alarmes',
        route: '#',
        iconName: 'alarms',
        children: [
          { label: 'Classificação', route: '/alarms/classification' },
          { label: 'Relatório', route: '/alarms/report' },
          { label: 'Listagem', route: '/alarms/listing' },
        ],
      },
      {
        label: 'Manutenções',
        route: '#',
        iconName: 'maintenances',
        activeMatchers: ['/maintenances/'],
        children: [
          { label: 'Minhas manutenções', route: '/maintenances/user-maintenances' },
          { label: 'Listagem', route: '/maintenances/listing' },
          { label: 'Criar', route: '/maintenances/create' },
          { label: 'Tipos', route: '/maintenances/create-types' },
          { label: 'Relatório', route: '/maintenances/report' },
        ],
      },
      {
        label: 'Gerencial',
        route: '#',
        iconName: 'management',
        activeMatchers: ['/management', '/daily-monitoring'],
        children: [
          { label: 'Acompanhamento Diário', route: '/daily-monitoring' },
          { label: 'Painel', route: '/management' },
          { label: 'Tarifas de energia', route: '/management/tariffs' },
        ],
      },
      {
        label: 'Empresa',
        route: '#',
        iconName: 'groups',
        children: [
          { label: 'Usuários', route: '/company-users' },
          { label: 'Turnos', route: '/company-shifts' }
        ],
      },
      {
        label: 'Otimizador de contrato',
        route: '/contract-optimizer',
        iconName: 'optimizer',
      },
      {
        label: 'Detecção de anomalias',
        route: '/anomaly-detector',
        iconName: 'anomaly_detector',
      },
]

const menuGroups = computed(() => [
  {
    name: '',
    menuItems: baseMenuItems
      .filter((item) => {
        if (item.label === 'Empresa' && !canAccessManagerRole.value) {
          return false
        }
        return true
      })
      .map((item) => {
        if (item.label !== 'Manutenções' || !item.children?.length) {
          return item
        }
        return {
          ...item,
          children: item.children.filter(
            (child) => child.route !== '/maintenances/report' || canAccessManagerRole.value,
          ),
        }
      })
  },
])
</script>

<template>
  <aside
    class="absolute left-0 top-0 z-9999 flex h-screen flex-col overflow-y-hidden bg-black duration-300 ease-linear dark:bg-boxdark lg:static lg:translate-x-0"
    :class="[
      sidebarStore.isSidebarCollapsed ? 'w-20' : 'w-72.5',
      {
        'translate-x-0': sidebarStore.isSidebarOpen,
        '-translate-x-full': !sidebarStore.isSidebarOpen,
      },
    ]"
    ref="target">
    <div class="hidden items-center justify-center border-b border-white/10 px-4 py-8 lg:flex">
      <router-link
        to="/"
        :class="
          sidebarStore.isSidebarCollapsed
            ? 'w-9 overflow-visible flex justify-center'
            : 'w-full overflow-hidden flex justify-center'
        ">
        <Logo class="my:2 h-6" color="#fff" />
      </router-link>
    </div>

    <CompanySelector :collapsed="sidebarStore.isSidebarCollapsed" />

    <div class="no-scrollbar flex-1 overflow-y-auto duration-300 ease-linear">
      <nav class="px-4 py-4 lg:px-4">
        <template v-for="menuGroup in menuGroups" :key="menuGroup.name">
          <div>
            <h3 v-if="!sidebarStore.isSidebarCollapsed" class="mb-4 font-bold text-bodydark2">
              {{ menuGroup.name }}
            </h3>

            <ul class="mb-6 flex flex-col gap-1.5">
              <SidebarItem
                v-for="(menuItem, index) in menuGroup.menuItems"
                :item="menuItem"
                :collapsed="sidebarStore.isSidebarCollapsed"
                :key="index" />
            </ul>
          </div>
        </template>
      </nav>
    </div>

    <div class="border-t border-white/10 p-4">
      <DropdownUser :compact="sidebarStore.isSidebarCollapsed" />
    </div>
  </aside>
</template>
